// IND-046: «Proveedores del recuento»: cada proveedor de la Hoja de recuento
// solo necesita su código del ERP. Pantalla aislada: no cambia stock ni desperdicio.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';

import { getRecountSuppliers, saveRecountSupplierCode } from '../services/industrialCostService';
import type { RecountSupplier, RecountSuppliersView } from '../services/industrialCostService';
import { isSupplierCode, supplierNote } from '../utils/recountSheet';
import { DetailError, DetailLoading } from './IndustrialCostMaterialDetailSections';

const MUTED = '#94a3b8';
const AMBER = '#fde68a';
const QUERY_KEY = ['industrial-cost-recount-suppliers'];
const GRID = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 70px 170px minmax(0, 1fr)', alignItems: 'center', px: 1.75 };
const FILTER = { height: 32, borderRadius: 999, textTransform: 'none', fontWeight: 700, fontSize: 12 };

export default function IndustrialCostSupplierMatchesView() {
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: getRecountSuppliers });
  return (
    <Stack spacing={1.75} sx={{ p: 2, minWidth: 0 }}>
      <Stack spacing={0.5}>
        <Typography sx={{ fontSize: 13, color: MUTED }}>Inventario · Hoja de recuento</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#f8fafc' }}>Proveedores del recuento</Typography>
        <Typography sx={{ fontSize: 14, color: MUTED }}>
          Cada proveedor de la hoja necesita su código de proveedor del ERP. Con el código, PROA puede sugerir compras y precio desde los albaranes.
        </Typography>
      </Stack>
      {query.isError ? (
        <DetailError error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : !query.data ? (
        <DetailLoading height={320} />
      ) : (
        <Content view={query.data} />
      )}
    </Stack>
  );
}

function Content({ view }: { view: RecountSuppliersView }) {
  const queryClient = useQueryClient();
  const [onlyMissing, setOnlyMissing] = useState(false);
  const save = useMutation({
    mutationFn: ({ supplier, code }: { supplier: string; code: string | null }) => saveRecountSupplierCode(supplier, code),
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-recount-sheet'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-recount-estimates'] }),
    ]),
  });

  if (!view.sheet) {
    return <Typography sx={{ color: MUTED, fontSize: 13 }}>Aún no hay Hoja de recuento: prepárala desde el libro.</Typography>;
  }
  const missing = view.total - view.with_code;
  const rows = onlyMissing ? view.suppliers.filter((item) => !item.code) : view.suppliers;
  return (
    <>
      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#f8fafc', fontVariantNumeric: 'tabular-nums' }}>
          {`${view.with_code} de ${view.total} con código`}
        </Typography>
        <Box sx={{ width: 240, height: 8, borderRadius: 999, bgcolor: 'rgba(148,163,184,0.2)' }}>
          <Box sx={{ width: `${view.total ? (view.with_code / view.total) * 100 : 0}%`, height: 8, borderRadius: 999, bgcolor: '#22d3ee' }} />
        </Box>
        <Button
          variant={onlyMissing ? 'contained' : 'outlined'}
          color="warning"
          size="small"
          onClick={() => setOnlyMissing(true)}
          sx={{ ...FILTER, ml: 'auto' }}
        >
          {`Sin código · ${missing}`}
        </Button>
        <Button variant={onlyMissing ? 'outlined' : 'contained'} size="small" onClick={() => setOnlyMissing(false)} sx={FILTER}>
          {`Todos · ${view.total}`}
        </Button>
      </Stack>
      {save.isError ? <Alert severity="error">No se pudo guardar el código. Vuelve a escribirlo.</Alert> : null}
      <Paper sx={{ bgcolor: '#151f30', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 3, boxShadow: 'none', overflow: 'hidden' }}>
        <Box sx={{ ...GRID, py: 1, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: MUTED, bgcolor: 'rgba(148,163,184,0.10)' }}>
          <span>PROVEEDOR EN EL RECUENTO</span>
          <Box component="span" sx={{ textAlign: 'right', pr: 1.75 }}>FILAS</Box>
          <span>CÓDIGO</span>
          <span>NOTA</span>
        </Box>
        <Box sx={{ maxHeight: 'calc(100vh - 300px)', minHeight: 200, overflowY: 'auto' }}>
          {rows.map((item) => (
            <SupplierRow key={item.key} item={item} onSave={(code) => save.mutate({ supplier: item.name, code })} />
          ))}
        </Box>
      </Paper>
      <Typography sx={{ fontSize: 12, color: '#64748b' }}>
        Solo se pide el código: no hace falta que el proveedor tenga albaranes. El código se escribe una vez y vale para todas sus filas.
      </Typography>
    </>
  );
}

function SupplierRow({ item, onSave }: { item: RecountSupplier; onSave: (code: string | null) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const text = (draft ?? item.code ?? '').trim();
  const wrong = text !== '' && !isSupplierCode(text);
  const pending = !item.code;
  const commit = () => {
    if (draft === null || wrong) return;
    setDraft(null);
    if (text !== (item.code ?? '')) onSave(text || null);
  };
  return (
    <Box sx={{ ...GRID, minHeight: 54, borderTop: '1px solid rgba(148,163,184,0.08)' }}>
      <Typography noWrap title={item.name} sx={{ fontSize: 14, fontWeight: 700, color: '#f8fafc' }}>{item.name}</Typography>
      <Box sx={{ textAlign: 'right', pr: 1.75, fontSize: 13, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{item.rows}</Box>
      <Box
        component="input"
        inputMode="numeric"
        maxLength={5}
        aria-label={`Código de ${item.name}`}
        placeholder={item.note === 'several' ? `${item.codes.join(' / ')} ?` : 'Escribe el código'}
        value={draft ?? item.code ?? ''}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        sx={{
          width: 140,
          height: 38,
          boxSizing: 'border-box',
          px: 1.25,
          borderRadius: 2,
          border: `1px solid ${wrong ? '#f87171' : pending ? 'rgba(251,191,36,0.6)' : 'rgba(148,163,184,0.3)'}`,
          bgcolor: '#0b1220',
          color: '#f8fafc',
          font: 'inherit',
          fontSize: 15,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          '&::placeholder': { color: '#64748b', fontSize: 13, fontWeight: 500 },
        }}
      />
      <Typography sx={{ fontSize: 12, color: wrong ? '#fca5a5' : pending ? AMBER : MUTED }}>
        {wrong ? 'Solo números, de 1 a 5 cifras' : supplierNote(item)}
      </Typography>
    </Box>
  );
}
